import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    List,
    ListItem,
    AppBar,
    Toolbar,
    CircularProgress,
    Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

interface Message {
    role: 'user' | 'model';
    text: string;
}

interface GeminiWidgetProps {
    apiKey: string; // Se mantiene por compatibilidad, aunque el server usa .env
    onClose: () => void;
}

const GeminiWidget: React.FC<GeminiWidgetProps> = ({ onClose }) => {
    const [messages, setMessages] = useState<Message[]>([
        { role: 'model', text: '¡Hola! Soy tu asistente médico. ¿En qué puedo ayudarte hoy?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        
        setError(null);
        const userText = input;
        setInput('');

        // Añadimos el mensaje del usuario y un espacio vacío para el modelo
        const userMessage: Message = { role: 'user', text: userText };
        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            // Formateamos el historial como espera el servidor (OpenAI/Llama style)
            const historyForServer = messages.map(m => ({
                role: m.role === 'user' ? 'user' : 'model',
                parts: [{ text: m.text }]
            }));

            const response = await fetch('https://luiatro-health-cloud-production.up.railway.app/api/gemini', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    message: userText,
                    history: historyForServer 
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Error en la comunicación con la IA");
            }

            // Añadimos la respuesta del modelo
            setMessages(prev => [...prev, { 
                role: 'model', 
                text: data.text || "No recibí una respuesta clara." 
            }]);
            
        } catch (error: any) {
            console.error("Error al enviar mensaje:", error);
            const errorMsg = error.message || "Error de conexión";
            setError(errorMsg);
            
            setMessages(prev => [...prev, { 
                role: 'model', 
                text: `⚠️ Error: ${errorMsg}. Por favor, verifica el servidor.` 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
            <AppBar position="static" elevation={0}>
                <Toolbar variant="dense">
                    <Typography variant="subtitle1" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                        Lumina AI Asistente
                    </Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            
            {error && (
                <Alert severity="warning" variant="filled" sx={{ m: 1, py: 0 }}>
                    {error}
                </Alert>
            )}
            
            <Box sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                p: 2, 
                minHeight: 0,
                overflow: 'hidden' 
            }}>
                <Paper variant="outlined" sx={{ 
                    flexGrow: 1, 
                    p: 1, 
                    overflowY: 'auto', 
                    bgcolor: '#fff',
                    display: 'flex',
                    flexDirection: 'column'
                }}> 
                    <List sx={{ width: '100%' }}>
                        {messages.map((msg, index) => (
                            <ListItem 
                                key={index} 
                                sx={{ 
                                    flexDirection: 'column',
                                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                    mb: 1,
                                    px: 1
                                }}
                            >
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: msg.role === 'user' ? 'primary.main' : '#f0f2f5',
                                        color: msg.role === 'user' ? 'white' : 'text.primary',
                                        borderRadius: msg.role === 'user' ? '15px 15px 0 15px' : '15px 15px 15px 0',
                                        maxWidth: '85%',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {msg.text}
                                    </Typography>
                                </Paper>
                            </ListItem>
                        ))}
                        <div ref={messagesEndRef} />
                    </List>
                </Paper>
                
                <Box sx={{ display: 'flex', pt: 2, gap: 1, alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Escriba aquí..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        disabled={isLoading}
                        sx={{ bgcolor: 'white' }}
                    />
                    <IconButton 
                        color="primary" 
                        onClick={handleSend} 
                        disabled={!input.trim() || isLoading}
                        sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default GeminiWidget;